import { useState } from 'react'

import { ShoppingCart } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { FilterMyClass } from '@/gql/graphql'

import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Heading } from '@/components/ui/typography'
import { TrackCardItem } from '@/components/view/track/track-card-item'
import { TrackProgressItem } from '@/components/view/track/track-progress'

import { useAuth } from '@/services/auth/auth.store'
import { trackList } from '@/services/track/track.gql'

export const Route = createFileRoute('/')({
  loader: () => {},
  component: App,
})

function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated)

  return (
    <div className="">
      {isAuthenticated && <ProgressKelas />}
      <AllTracks />
    </div>
  )
}

function ProgressKelas() {
  // const isAuthenticated = useAuth(s => s.isAuthenticated)
  const [filter, setFilter] = useState<FilterMyClass>('All')
  const { data, isLoading } = useQuery(
    trackList.qry({ filterMyClass: filter, myClass: true })
  )

  const isEmptyList = !isLoading && !data?.getTracksWithPagination.data.length

  return (
    <div className="container mx-auto flex grow flex-col py-16">
      <Heading className="self-center" level="h2">
        Progress Kelas
      </Heading>

      <Tabs
        className="mt-8 self-center"
        value={filter}
        onValueChange={(val) => setFilter(val as FilterMyClass)}
      >
        <TabsList style={{ width: 'calc(140px*3)' }}>
          <TabsTrigger value="All">Semua</TabsTrigger>
          <TabsTrigger value="OnProgress">Dalam Progress</TabsTrigger>
          <TabsTrigger value="Done">Selesai</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mx-auto mt-4 grid w-full grid-cols-1 md:grid-cols-2">
        {isLoading && <LoadingList />}
        {data &&
          !isEmptyList &&
          data.getTracksWithPagination.data.map((track) => (
            <TrackProgressItem
              key={track.id}
              id={track.id}
              title={track.title}
              img={track.bannerIconURL!}
              categories={[track.track_category?.category].filter(Boolean)}
              progressPercentage={track.percentage_progress || 0}
            />
          ))}
        {isEmptyList && <EmptyList filter={filter} />}
      </div>
    </div>
  )
}

function LoadingList() {
  return (
    <div className="col-span-full flex grow flex-wrap">
      {Array.from({ length: 10 }).map((_, i) => (
        <TrackProgressItem isLoading />
      ))}
    </div>
  )
}

interface EmptyListProps {
  filter: FilterMyClass
}

function EmptyList({ filter }: EmptyListProps) {
  const isDone = filter === 'Done'

  return (
    <div className="border-persian-red-200 bg-persian-red-50 col-span-full mx-auto flex min-h-56 w-full max-w-2xl flex-1 grow flex-col items-center justify-center rounded-lg border p-4">
      <div>
        <Heading level={'h5'} className="text-center">
          {isDone
            ? 'Belum ada kelas yang selesai'
            : 'Kamu belum memiliki kelas yang sedang berlangsung'}
        </Heading>
        {!isDone && (
          <p className="mt-1 text-center">
            Ayo mulai belajar bersama dengan YES Academy
          </p>
        )}
      </div>
      {!isDone && (
        <Button variant={'yes'} className="mt-6">
          <ShoppingCart /> Beli Kelas Sekarang
        </Button>
      )}
    </div>
  )
}

function AllTracks() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated)
  const allMyClass = useQuery({
    ...trackList.qry({ filterMyClass: 'All', myClass: true }),
    enabled: isAuthenticated,
  })

  const { data, isLoading } = useQuery({
    ...trackList.qry({ filterMyClass: 'All', myClass: false }),
    enabled: (isAuthenticated && allMyClass.isFetched) || !isAuthenticated,
  })

  const myClassIds =
    allMyClass.data?.getTracksWithPagination.data.map((t) => t.id) || []
  const filteredData = data?.getTracksWithPagination.data.filter(
    (t) => !myClassIds.includes(t.id)
  )

  const isEmptyList = !isLoading && !filteredData

  return (
    <div className="container mx-auto flex grow flex-col py-16">
      <Heading className="max-w-sm self-center text-center" level="h2">
        Kelas yang mungkin kamu minati
      </Heading>

      <div className="mx-auto mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
        {filteredData &&
          !isEmptyList &&
          filteredData.map((track) => (
            <TrackCardItem
              key={track.id}
              id={track.id}
              title={track.title}
              img={track.bannerIconURL!}
              categories={[track.track_category?.category].filter(Boolean)}
              price={track.price}
            />
          ))}
        {isEmptyList && <EmptyList filter="All" />}
      </div>
    </div>
  )
}
